import './applications.css';
import { accessViewToMatrix, safeStr } from './applicationDetailShared.js';
import { domainLabel } from './parseCapabilityURN.js';

// AccessTab — read-only grant matrix (JOBE-0148.2).

// buildDomainGroups assumes `urns` is already sorted alphabetical-by-domain
// then alphabetical-by-action (accessViewToMatrix guarantees this), so a
// single linear pass produces contiguous group boundaries.
function buildDomainGroups(urns) {
  const groups = [];
  let current = null;
  for (const u of urns) {
    const domainKey = u.domain || '(other)';
    if (!current || current.domain !== domainKey) {
      current = { domain: domainKey, label: u.domain ? domainLabel(u.domain) : '(Other)', urns: [] };
      groups.push(current);
    }
    current.urns.push(u);
  }
  return groups;
}

// allowCountsByHandle returns a Map<principal.handle, number> with the count
// of 'allow' grants per principal — single pass over the grants matrix so the
// downstream with/without-access partitions and the summary all share one
// scan instead of re-filtering per principal.
function allowCountsByHandle(principals, urns, grants) {
  const counts = new Map();
  for (const p of principals) {
    let n = 0;
    for (const u of urns) {
      if (grants[`${p.handle}|${u.urn}`] === 'allow') n += 1;
    }
    counts.set(p.handle, n);
  }
  return counts;
}

// Summary line per spec §Access summary header: truncate to 3 inline names +
// "+N more"; return null when zero principals have access so the caller can
// hide the line entirely.
function buildAccessSummary(principals, allowCounts) {
  const withAccess = principals
    .filter((p) => allowCounts.get(p.handle) > 0)
    .map((p) => ({ name: safeStr(p.title) || safeStr(p.handle) || '', count: allowCounts.get(p.handle) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (withAccess.length === 0) return null;

  const M = principals.length;
  const N = withAccess.length;

  const MAX_INLINE = 3;
  const shown = withAccess.slice(0, MAX_INLINE);
  const overflow = withAccess.length - MAX_INLINE;

  let namePart = shown.map((p) => `${p.name} (${p.count})`).join(', ');
  if (overflow > 0) namePart += `, … +${overflow} more`;

  return `${N} of ${M} ${M === 1 ? 'principal' : 'principals'} have at least one capability granted — ${namePart}`;
}

function renderPrincipalCell(p) {
  const isAgent = p.kind === 'agent';
  const initials = (safeStr(p.title) || safeStr(p.handle) || '?').slice(0, 2).toUpperCase();
  const label = safeStr(p.title) || safeStr(p.handle);
  return (
    <span className={`prince${isAgent ? ' agent' : ''}`}>
      <span className="av">{initials}</span>
      {label}
    </span>
  );
}

function renderGrantCell(p, u, grants) {
  const decision = grants[`${p.handle}|${u.urn}`];
  if (decision === 'allow') {
    return (
      <span className="access-allow-dot" title={`${u.urn} · allow`}>
        <span className="access-allow-dot-circle" aria-hidden="true">●</span>
        <span className="access-allow-dot-text">Allow</span>
      </span>
    );
  }
  return <span className="access-deny-dash" title={`${u.urn} · ${decision || 'absent'}`}>—</span>;
}

// isGroupSeam: true on the last column of any non-last domain group, used to
// paint the vertical 1px --rule-2 seam between groups (per Decision 11).
function isGroupSeam(gi, ui, group, groupCount) {
  return ui === group.urns.length - 1 && gi < groupCount - 1;
}

function PrincipalRow({ p, rowClassName, domainGroups, grants }) {
  return (
    <tr className={rowClassName}>
      <td className="accessPrincipalCol accessPrincipalCell">
        {renderPrincipalCell(p)}
      </td>
      {domainGroups.map((g, gi) =>
        g.urns.map((u, ui) => (
          <td
            key={u.urn}
            className={`accessGrantCell${isGroupSeam(gi, ui, g, domainGroups.length) ? ' accessGroupSeam' : ''}`}
          >
            {renderGrantCell(p, u, grants)}
          </td>
        )),
      )}
    </tr>
  );
}

export function AccessTab({ access }) {
  if (access.status === 'loading') {
    return (
      <div style={{ padding: '48px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', textAlign: 'center' }}>
        loading…
      </div>
    );
  }
  if (access.status === 'error') {
    return (
      <div style={{ padding: '48px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', textAlign: 'center' }}>
        <div style={{ marginBottom: 8 }}>Could not load access matrix.</div>
        <div style={{ fontSize: 11 }}>{access.error}</div>
      </div>
    );
  }

  const { principals, urns, grants } = accessViewToMatrix(access.data);

  if (urns.length === 0) {
    return (
      <div style={{ padding: '48px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', textAlign: 'center', fontStyle: 'italic' }}>
        no capabilities to display
      </div>
    );
  }
  if (principals.length === 0) {
    return (
      <div style={{ padding: '48px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', textAlign: 'center', fontStyle: 'italic' }}>
        no principals to display
      </div>
    );
  }

  const allowCounts = allowCountsByHandle(principals, urns, grants);
  const anyAllow = Array.from(allowCounts.values()).some((n) => n > 0);

  // All-denied: skip the matrix entirely per spec §All-denied empty state.
  // 81 uniform deny cells is visual noise, not a policy view.
  if (!anyAllow) {
    return (
      <div className="accessTab">
        <div className="accessEmptyState">
          <strong>No access granted</strong>
          {' — '}
          All principals are denied all capabilities under the current policy.
        </div>
      </div>
    );
  }

  const sortByName = (a, b) => (safeStr(a.title) || safeStr(a.handle) || '').localeCompare(safeStr(b.title) || safeStr(b.handle) || '');
  const withAccess = principals.filter((p) => allowCounts.get(p.handle) > 0).sort(sortByName);
  const withoutAccess = principals.filter((p) => allowCounts.get(p.handle) === 0).sort(sortByName);

  const domainGroups = buildDomainGroups(urns);
  const summary = buildAccessSummary(principals, allowCounts);

  return (
    <div className="accessTab">
      {summary && <div className="accessSummary">{summary}</div>}

      <div className="accessTableWrap">
        <table className="accessMatrix">
          <thead>
            <tr className="accessTheadDomain">
              <th className="accessPrincipalCol" />
              {domainGroups.map((g, gi) => (
                <th
                  key={g.domain}
                  colSpan={g.urns.length}
                  className={`accessDomainHeader${gi < domainGroups.length - 1 ? ' accessGroupSeam' : ''}`}
                >
                  {g.label.toUpperCase()}
                </th>
              ))}
            </tr>
            <tr className="accessTheadAction">
              <th className="accessPrincipalCol">Principal</th>
              {domainGroups.map((g, gi) =>
                g.urns.map((u, ui) => (
                  <th
                    key={u.urn}
                    title={u.urn}
                    className={`accessActionHeader${isGroupSeam(gi, ui, g, domainGroups.length) ? ' accessGroupSeam' : ''}`}
                  >
                    {u.action ? domainLabel(u.action) : (safeStr(u.label) || safeStr(u.urn))}
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {withAccess.map((p) => (
              <PrincipalRow
                key={p.handle}
                p={p}
                rowClassName="accessRowWithAccess"
                domainGroups={domainGroups}
                grants={grants}
              />
            ))}

            {withoutAccess.length > 0 && (
              <tr className="accessSeparatorRow">
                <td colSpan={1 + urns.length} className="accessSeparatorCell">
                  No access · {withoutAccess.length} {withoutAccess.length === 1 ? 'principal' : 'principals'}
                </td>
              </tr>
            )}

            {withoutAccess.map((p) => (
              <PrincipalRow
                key={p.handle}
                p={p}
                rowClassName="accessRowWithoutAccess"
                domainGroups={domainGroups}
                grants={grants}
              />
            ))}
          </tbody>
        </table>
      </div>
      {/* TODO(JOBE-0148-followup): wire Cedar source URL */}
    </div>
  );
}
